import { brand } from '../../lib/brand';
import { useCallback, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, apiClient } from '../../lib/api';
import { safeFormatDate } from '../../lib/format';
import { useSiteName } from '../../hooks/useSiteName';

interface BrandingItem {
  id: string;
  key: string;
  filePath: string;
  originalName: string;
  mimeType: string;
  updatedAt: string;
}

const BRANDING_ITEMS = [
  {
    key: 'login_logo',
    labelKey: 'branding_login_logo',
    size: '280×80px',
    descKey: 'branding_login_logo_desc',
  },
  {
    key: 'header_logo',
    labelKey: 'branding_header_logo',
    size: '160×40px',
    descKey: 'branding_header_logo_desc',
  },
  {
    key: 'favicon',
    labelKey: 'branding_favicon',
    size: '32×32px',
    descKey: 'branding_favicon_desc',
  },
  {
    key: 'tv_logo',
    labelKey: 'branding_tv_logo',
    size: '400×120px',
    descKey: 'branding_tv_logo_desc',
  },
  {
    key: 'receipt_logo',
    labelKey: 'branding_receipt_logo',
    size: '200×60px',
    descKey: 'branding_receipt_logo_desc',
  },
  {
    key: 'pwa_icon',
    labelKey: 'branding_pwa_icon',
    size: '512×512px',
    descKey: 'branding_pwa_icon_desc',
  },
];

function BrandingCard({
  config,
  branding,
}: {
  config: (typeof BRANDING_ITEMS)[0];
  branding: BrandingItem | null;
}) {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  const [dragOver, setDragOver] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      await apiClient.post(`/branding/${config.key}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branding'] });
    },
    onError: (err) => {
      console.warn('[Branding] upload failed:', (err as Error).message);
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => api.delete(`/branding/${config.key}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branding'] });
    },
    onError: (err) => {
      console.warn('[Branding] reset failed:', (err as Error).message);
    },
  });

  const handleFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith('image/')) {
        alert(t('brandingOnlyImages'));
        return;
      }
      uploadMutation.mutate(file);
    },
    [uploadMutation],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const imageUrl = branding ? `/api/branding/${config.key}/file` : null;

  return (
    <div className="bg-base-200 border border-base-300 rounded-xl p-5 space-y-4">
      <div>
        <h3 className="font-semibold text-base-content">{t(config.labelKey)}</h3>
        <p className="text-xs text-base-content/50 mt-0.5">{t(config.descKey)}</p>
        <p className="text-xs text-primary/60">
          {t('brandingSuggestedSize', { size: config.size })}
        </p>
      </div>

      {/* Preview */}
      <div className="bg-base-300 rounded-lg p-4 min-h-[80px] flex items-center justify-center">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={t(config.labelKey)}
            className="max-h-20 max-w-full object-contain"
          />
        ) : (
          <div className="text-center text-base-content/30">
            <span className="material-symbols-rounded text-3xl block">image</span>
            <span className="text-xs">{t('brandingDefaultImage')}</span>
          </div>
        )}
      </div>

      {/* Upload Zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-4 text-center transition cursor-pointer ${
          dragOver ? 'border-primary bg-primary/10' : 'border-base-300 hover:border-primary/50'
        }`}
        onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/png,image/jpeg,image/svg+xml';
          input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) handleFile(file);
          };
          input.click();
        }}
      >
        {uploadMutation.isPending ? (
          <span className="text-sm text-primary">{t('brandingUploading')}</span>
        ) : (
          <span className="text-sm text-base-content/50">{t('brandingDropOrClick')}</span>
        )}
      </div>

      {/* Reset button */}
      {branding && (
        <button
          onClick={() => {
            if (confirm(t('brandingConfirmReset'))) resetMutation.mutate();
          }}
          disabled={resetMutation.isPending}
          className="w-full py-2 rounded-lg border border-base-300 text-sm text-base-content/60 hover:bg-base-300 transition"
        >
          {t('brandingResetDefault')}
        </button>
      )}

      {branding && (
        <p className="text-[10px] text-base-content/30 truncate">
          {branding.originalName} •{' '}
          {safeFormatDate(branding.updatedAt, (d) => d.toLocaleDateString('zh-TW'))}
        </p>
      )}
    </div>
  );
}

function SiteNameCard() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const currentName = useSiteName();

  const { data: nameSetting } = useQuery<{ key: string; value: string } | null>({
    queryKey: ['settings', 'site_name'],
    queryFn: async () => {
      try {
        return await api.get<{ key: string; value: string }>('/settings/site_name');
      } catch {
        return null;
      }
    },
  });

  const [siteName, setSiteName] = useState('');
  useEffect(() => {
    if (nameSetting?.value !== undefined) setSiteName(nameSetting.value);
  }, [nameSetting]);

  const [saveError, setSaveError] = useState('');

  const mutation = useMutation({
    mutationFn: (value: string) => api.put('/settings/site_name', { value }),
    onSuccess: () => {
      setSaveError('');
      qc.invalidateQueries({ queryKey: ['settings', 'site_name'] });
    },
    onError: (err: Error) => {
      setSaveError(err.message || '儲存失敗');
    },
  });

  return (
    <div className="bg-base-200 border border-base-300 rounded-xl p-5 space-y-4">
      <div>
        <h3 className="font-semibold text-base-content">{t('brandingSiteNameLabel')}</h3>
        <p className="text-xs text-base-content/50 mt-0.5">{t('brandingSiteNameDesc')}</p>
      </div>
      {saveError && (
        <div className="p-3 bg-error/10 border border-error/30 rounded-lg text-error text-sm">
          {saveError}
        </div>
      )}
      <div className="bg-base-300 rounded-lg p-4 min-h-[80px] flex items-center justify-center">
        <span className="text-xl font-bold text-base-content heading-font tracking-wide">
          {currentName}
        </span>
      </div>
      <div className="flex gap-3">
        <input
          type="text"
          value={siteName}
          onChange={(e) => setSiteName(e.target.value)}
          placeholder={brand.name}
          className="input input-bordered flex-1 bg-base-300 text-base-content"
        />
        <button
          onClick={() => mutation.mutate(siteName)}
          disabled={mutation.isPending}
          className="btn btn-primary btn-sm px-4"
        >
          {mutation.isPending ? t('saving') : tc('save')}
        </button>
      </div>
      {mutation.isSuccess && <p className="text-xs text-success">{t('pageSettingsSaved')}</p>}
    </div>
  );
}

export default function BrandingPage() {
  const { t } = useTranslation('admin');
  const { data: brandings = [] } = useQuery<BrandingItem[]>({
    queryKey: ['branding'],
    queryFn: () => api.get<BrandingItem[]>('/branding'),
  });

  const brandingMap = new Map(brandings.map((b) => [b.key, b]));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-base-content mb-2">{t('brandingTitle')}</h1>
      <p className="text-sm text-base-content/50 mb-6">{t('brandingSubtitle')}</p>

      <SiteNameCard />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {BRANDING_ITEMS.map((config) => (
          <BrandingCard
            key={config.key}
            config={config}
            branding={brandingMap.get(config.key) ?? null}
          />
        ))}
      </div>
    </div>
  );
}
